import { usePageMeta } from "../hooks/usePageMeta";
import { FadeIn } from "../components/site/FadeIn";
import { SectionHeading } from "../components/site/SectionHeading";
import { RESEARCH_QUESTIONS } from "../data/config";

const STEPS = [
  { num: "01", title: "Design and build", copy: "The lab employs developers, so a concept becomes a working artefact rather than a specification. Nothing in this practice is evaluated as a mock-up." },
  { num: "02", title: "Co-design with the people who will use it", copy: "Teachers select content. Health workers shape the training sequence. Partially sighted consumers write the specification for immersive retail. The user population sets the vocabulary, it is not assumed." },
  { num: "03", title: "Install at a real site", copy: "A government classroom, a village sub-centre, a planetarium, a kirana store. The system has to survive the absent network, the shared room and the missing accessory before a single measurement is possible." },
  { num: "04", title: "Measure, and report what worked & failed", copy: "Pre-test, post-test, delayed retention, operational timing. Then publish the non-significant module alongside the significant one." },
];

export default function Research() {
  usePageMeta(
    "Research — Keyur Sorathia",
    "Practice-based research on cultural transfer, longitudinal adoption, and room-scale and hybrid immersion in immersive learning. Field studies with measured outcomes."
  );
  return (
    <div data-testid="research-page">
      <section className="section">
        <div className="container-site">
          <FadeIn>
            <h1 className="text-h1 mb-6" style={{ color: "var(--navy-700)", maxWidth: 900 }}>
              Immersive learning works in a laboratory. I work on what survives outside one.
            </h1>
            <p className="body-lg measure">
              My research is practice-based. A system is designed and built, co-designed with the people who will use it, installed at a real site, and then measured. Everything below rests on a deployment that actually happened.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="section" style={{ background: "var(--surface-alt)" }}>
        <div className="container-site">
          <FadeIn>
            <SectionHeading eyebrow="How it works" title="Four steps, every project" />
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s) => (
              <FadeIn key={s.num}>
                <div className="h-full bg-white p-6" style={{ border: "1px solid var(--border-default)", borderRadius: 10 }} data-testid={`research-step-${s.num}`}>
                  <p className="caption m-0 mb-2 font-semibold" style={{ color: "var(--accent-teal)" }}>{s.num}</p>
                  <h3 className="text-h4 mb-3">{s.title}</h3>
                  <p className="body-sm m-0">{s.copy}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-site">
          <FadeIn>
            <SectionHeading eyebrow="Three threads" title="Three questions about immersive learning that the field has not answered." />
          </FadeIn>
          <div className="flex flex-col gap-16">
            {RESEARCH_QUESTIONS.map((q) => (
              <FadeIn key={q.number}>
                <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8" data-testid={`research-thread-${q.number}`}>
                  <div>
                    <div style={{ height: 4, width: 48, background: "var(--accent-teal)" }} className="mb-4" />
                    <p className="caption m-0 mb-2 font-semibold" style={{ color: "var(--accent-teal)" }}>{q.number}</p>
                    <h3 className="text-h3 mb-3" style={{ color: "var(--navy-700)" }}>{q.title}</h3>
                    <p className="body-default m-0 font-medium" style={{ color: "var(--ink-900)" }}>{q.question}</p>
                  </div>
                  <div className="flex flex-col gap-6">
                    <div>
                      <p className="eyebrow m-0 mb-2" style={{ color: "var(--navy-500)" }}>What I have shown so far</p>
                      <p className="body-default m-0 measure">{q.shown}</p>
                    </div>
                    <div>
                      <p className="eyebrow m-0 mb-2" style={{ color: "var(--navy-500)" }}>What is still open</p>
                      <p className="body-default m-0 measure">{q.open}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
